import { existsSync } from "node:fs";
import { appendFile, mkdir, readFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import { exclusiveCreateFile, validateDataRelativePath } from "@vortex-os/core";

/**
 * Hardened, deterministic write helper for the curate value loop.
 *
 * This is the single gate every document write goes through — the
 * insight-proposer's `onAccept` and the `vortex curate-accept` CLI subcommand
 * both call {@link writeDocAction}. It does NOT decide *whether* to write
 * (that is the user's accept) and it does NOT consult an LLM. It takes a
 * concrete document action and either creates a new file (exclusive — refuses
 * to overwrite) or appends a section to an EXISTING markdown file.
 *
 * Path safety is enforced first, for BOTH actions, via
 * {@link validateDataRelativePath} from `@vortex-os/core`. A path that escapes
 * `data/`, is absolute/drive-qualified, or targets a system/meta directory is
 * rejected before any filesystem access — closing the path-traversal gap that
 * was previously only guarded by the insight-proposer's system-meta string
 * check.
 *
 * v1 scope (consensus): create-file + append-section only. No update-file
 * (whole-file replace = data loss) and no create-folder distinct path
 * (create-file's mkdir covers the new-folder case).
 */

/** A document write the user has accepted. Path is `data/`-relative. */
export type DocWriteAction =
  | {
      readonly kind: "create-file";
      /** `data/`-relative path of the file to create (validated here). */
      readonly targetRelPath: string;
      readonly body: string;
    }
  | {
      readonly kind: "append-section";
      /** `data/`-relative path of an EXISTING file to append to (validated here). */
      readonly targetRelPath: string;
      readonly sectionHeader: string;
      readonly body: string;
    };

export interface WriteDocResult {
  /** Absolute path written. */
  readonly writtenPath: string;
  readonly kind: DocWriteAction["kind"];
}

/**
 * Apply a document write action against `<cwd>/data`, with path validation and
 * exclusive-create / append-to-existing semantics.
 *
 *  - `create-file`: validate the path, `mkdir -p` the parent, then write with
 *    {@link exclusiveCreateFile} (throws "refusing to overwrite" if it exists).
 *  - `append-section`: validate the path, REQUIRE the file to already exist
 *    (append-section never creates), then append a `## <sectionHeader>` block.
 *
 * @throws if the path is unsafe, if create-file's target exists, or if
 *         append-section's target does not exist.
 */
export async function writeDocAction(
  cwd: string,
  action: DocWriteAction,
): Promise<WriteDocResult> {
  const dataDir = join(cwd, "data");
  const abs = validateDataRelativePath(dataDir, action.targetRelPath);

  if (action.kind === "create-file") {
    await mkdir(dirname(abs), { recursive: true });
    await exclusiveCreateFile(abs, action.body);
    return { writtenPath: abs, kind: "create-file" };
  }

  // append-section — only ever appends to an existing file.
  if (!existsSync(abs)) {
    throw new Error(
      `Cannot append-section: target file does not exist: ${action.targetRelPath}. ` +
        "append-section adds to an existing document; use create-file for a new one.",
    );
  }
  const existing = await readFile(abs, "utf8");
  const newline = existing.length > 0 && !existing.endsWith("\n") ? "\n" : "";
  const section = `${newline}\n## ${action.sectionHeader}\n${action.body}`;
  await appendFile(abs, section, "utf8");
  return { writtenPath: abs, kind: "append-section" };
}
